import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useDispatch, useSelector } from "react-redux";
import {
  FaFacebookF,
  FaTwitter,
  FaWhatsapp,
  FaLinkedinIn,
  FaTelegramPlane,
} from "react-icons/fa";
import { fetchPostById } from "../redux/slices/postSlice";
import { addPostShareApi } from "../api/PostShareActions";
import { addPostViewApi } from "../api/ClientActions";

const FaIcon = ({ icon: Icon, size = 15 }: any) => {
  return <Icon size={size} />;
};

const SITE_URL = (
  process.env.REACT_APP_WEBSITE_URL ||
  "https://www.bsangam.com"
).replace(/\/$/, "");

const API_URL = (
  process.env.REACT_APP_API_URL ||
  "https://api.bsangam.com"
)
  .replace(/\/uploads\/?$/, "")
  .replace(/\/$/, "");

const getUploadUrl = (file?: string | null) => {
  if (!file || !String(file).trim()) {
    return "";
  }

  const value = String(file).trim();

  if (
    value.startsWith("http://") ||
    value.startsWith("https://")
  ) {
    return encodeURI(value);
  }

  if (value.startsWith("/uploads/")) {
    return encodeURI(`${API_URL}${value}`);
  }

  if (value.startsWith("uploads/")) {
    return encodeURI(`${API_URL}/${value}`);
  }

  if (value.startsWith("/")) {
    return encodeURI(`${API_URL}${value}`);
  }

  return encodeURI(
    `${API_URL}/uploads/${value}`
  );
};

const cleanHtml = (html = "") => {
  return html
    .replace(/<img[^>]*src=["']\/\/:0["'][^>]*>/gi, "")
    .replace(/<img[^>]*src=["']about:blank["'][^>]*>/gi, "")
    .replace(/src=["']\/uploads\//gi, `src="${API_URL}/uploads/`)
    .replace(/&nbsp;/g, " ")
    .replace(/&#39;/g, "'")
    .replace(/&amp;/g, "&");
};

const removeImagesFromHtml = (html = "") => {
  return html.replace(/<img[^>]*>/gi, "").trim();
};

const getPlainText = (html = "") => {
  return html.replace(/<[^>]*>/g, "").replace(/\s+/g, " ").trim();
};

const getYoutubeEmbedUrl = (url?: string | null) => {
  if (!url) return "";

  const match = url.match(
    /(?:youtube\.com\/watch\?v=|youtube\.com\/shorts\/|youtu\.be\/)([^&?/]+)/i
  );

  return match?.[1] ? `https://www.youtube.com/embed/${match[1]}` : "";
};


const getVisitorKey = () => {
  const storageKey = "bs_visitor_key";

  let visitorKey = localStorage.getItem(storageKey);

  if (!visitorKey) {
    visitorKey = `guest_${Date.now()}_${Math.random()
      .toString(36)
      .substring(2, 12)}`;

    localStorage.setItem(storageKey, visitorKey);
  }

  return visitorKey;
};

const getLoggedInUserId = () => {
  try {
    const storedUser = localStorage.getItem("user");

    if (!storedUser) {
      return null;
    }

    const user = JSON.parse(storedUser);
    const userId = Number(user?.user_id);

    return Number.isInteger(userId) && userId > 0
      ? userId
      : null;
  } catch {
    return null;
  }
};

const NewsDetails: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<any>();
  const { id } = useParams();

  const [shareOpen, setShareOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showComments, setShowComments] = useState(false);
  const [shareCount, setShareCount] = useState(0);

  const { selectedPost: post, detailLoading } = useSelector(
    (state: any) => state.posts
  );

  useEffect(() => {
    if (id) {
      dispatch(fetchPostById(Number(id)));
    }
  }, [dispatch, id]);

  useEffect(() => {
    if (!id) {
      return;
    }

    const postId = Number(id);

    if (
      !Number.isInteger(postId) ||
      postId <= 0
    ) {
      return;
    }

    const recordView = async () => {
      try {
        const userId = getLoggedInUserId();

        if (userId) {
          await addPostViewApi({
            post_id: postId,
            user_id: userId,
            platform: "web",
          });

          return;
        }

        const visitorKey = getVisitorKey();

        await addPostViewApi({
          post_id: postId,
          visitor_key: visitorKey,
          platform: "web",
        });
      } catch (error) {
        console.error(
          "Post view error:",
          error
        );
      }
    };

    void recordView();
  }, [id]);

  useEffect(() => {
    setShareCount(Number(post?.share_count || 0));
  }, [post?.share_count]);

  if (detailLoading) {
    return (
      <div className="news-state-page">
        <div className="news-state-card">
          <div className="news-loader"></div>
          <h3>Loading post</h3>
          <p>Please wait while we fetch the post details.</p>
        </div>

        <style>{stateStyle}</style>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="news-state-page">
        <div className="news-state-card">
          <div className="news-empty-icon">!</div>

          <h3>No post details found</h3>
          <p>This post may be removed or the link is invalid.</p>

          <button
            type="button"
            className="news-state-btn"
            onClick={() => navigate("/home", { replace: true })}
          >
            Go Home
          </button>
        </div>

        <style>{stateStyle}</style>
      </div>
    );
  }

  const content = cleanHtml(post.post_content || "");
  const youtubeEmbedUrl = getYoutubeEmbedUrl(post.youtube_url);

  const postImage = getUploadUrl(post.post_image);
  const postVideo = getUploadUrl(post.post_video);
  const clubLogo =
    getUploadUrl(post.logo_image) || "/assets/images/user.png";

  const mediaType = postImage
    ? "image"
    : youtubeEmbedUrl
    ? "youtube"
    : postVideo
    ? "video"
    : content.includes("<img")
    ? "content-image"
    : "text";

  const textContent =
    mediaType === "content-image" ? content : removeImagesFromHtml(content);

  const pageTitle = post.title || post.short_description || "Bharat Sangam";

  const pageDescription =
    post.short_description ||
    getPlainText(post.post_content || "").slice(0, 160) ||
    "Bharat Sangam Community Platform";

  const postId = post.post_id || post.id || id;

  const shareUrl = `${API_URL}/share/news-details/${postId}`;
  const metaImage = postImage || `${SITE_URL}/assets/images/bharat_sangam.png`;

  const encodedShareUrl = encodeURIComponent(shareUrl);
  const encodedTitle = encodeURIComponent(pageTitle);

  const updateShareCount = async (
    shareType: string
  ) => {
    const clientId = Number(
      post.client_id
    );

    if (!postId || !clientId) {
      console.error(
        "post_id or client_id is missing"
      );
      return null;
    }

    try {
      const result =
        await addPostShareApi({
          post_id: postId,
          client_id: clientId,
          share_type: shareType,
        });

      if (
        result?.success === false ||
        result?.status === false
      ) {
        throw new Error(
          result?.message ||
          "Failed to update share count"
        );
      }

      const updatedShareCount =
        Number(
          result?.data?.share_count ??
          shareCount + 1
        );

      setShareCount(
        updatedShareCount
      );

      return result;
    } catch (error) {
      console.error(
        "Share count update error:",
        error
      );

      return null;
    }
  };

  const openShare = (
    url: string,
    shareType: string
  ) => {
    window.open(
      url,
      "_blank",
      "width=700,height=500,noopener,noreferrer"
    );

    setShareOpen(false);
    void updateShareCount(shareType);
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      await updateShareCount("copy_link");

      setCopied(true);

      setTimeout(() => {
        setCopied(false);
      }, 1500);
    } catch (error) {
      console.error("Unable to copy share link:", error);
    }
  };

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>

        <meta name="description" content={pageDescription} />

        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:image" content={metaImage} />
        <meta property="og:image:secure_url" content={metaImage} />
        <meta property="og:url" content={shareUrl} />
        <meta property="og:type" content="article" />
        <meta property="og:site_name" content="Bharat Sangam" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        <meta name="twitter:image" content={metaImage} />
      </Helmet>

      <div className="news-details-page">
        <div className="news-details-card">
          <div className="news-details-top">
            <button
              type="button"
              className="news-back-btn"
              onClick={() => navigate("/home", { replace: true })}
            >
              <span>←</span>
              Back
            </button>
          </div>

          <div className="news-details-header">
            <img
              src={clubLogo}
              className="news-details-avatar"
              alt={post.client_name || "Club logo"}
              onError={(event) => {
                event.currentTarget.onerror = null;
                event.currentTarget.src = "/assets/images/user.png";
              }}
            />

            <div className="news-source-info">
              <h4>{post.client_name || "News Source"}</h4>

              {post.created_at && (
                <p>{new Date(post.created_at).toLocaleString("en-IN")}</p>
              )}
            </div>
          </div>

          <h1 className="news-details-title">{pageTitle}</h1>

          {post.short_description && (
            <p className="news-details-short">{post.short_description}</p>
          )}

          {mediaType === "image" && (
            <div className="news-details-media">
              <img src={postImage} alt={pageTitle} />
            </div>
          )}

          {mediaType === "youtube" && (
            <div className="news-details-youtube">
              <iframe
                src={youtubeEmbedUrl}
                title={post.title || "YouTube video"}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
              />
            </div>
          )}

          {mediaType === "video" && (
            <div className="news-details-media">
              <video src={postVideo} controls />
            </div>
          )}

          {textContent && (
            <div
              className="news-details-content"
              dangerouslySetInnerHTML={{ __html: textContent }}
            />
          )}

          {post.gallery_images?.length > 0 && (
            <div className="news-details-gallery">
              {post.gallery_images.map((img: any) => (
                <img
                  key={img.image_id || img.image}
                  src={getUploadUrl(img.image || img.url)}
                  alt="gallery"
                />
              ))}
            </div>
          )}

          {post.tags?.length > 0 && (
            <div className="news-details-tags">
              {post.tags.map((tag: any) => (
                <span
                  key={tag.tag_id}
                  style={{
                    color: tag.tag_color || "#2563eb",
                    borderColor: tag.tag_color || "#c7d2fe",
                  }}
                >
                  #{tag.tag_name}
                </span>
              ))}
            </div>
          )}

          {String(post.main_category || "")
            .trim()
            .toLowerCase() === "donation" && (
            <div className="news-donation-action">
              <button
                type="button"
                className="news-donate-btn"
                onClick={() =>
                  navigate(`/donate/${postId}`, {
                    state: {
                      post,
                      donationFrom:
                        post.unique_slug || "public",
                    },
                  })
                }
              >
                <span>₹</span>
                Donate Now
              </button>
            </div>
          )}

          <div className="news-details-stats">
            <span className="stat-item">
              <i className="feather-eye"></i>
              <span>{Number(post.view_count || 0)}</span>
            </span>

            <button type="button" className="stat-btn like-active">
              <span className="full-heart">♥</span>
              <span>{Number(post.like_count || post.reaction_count || 0)}</span>
            </button>

            <button
              type="button"
              className={`stat-btn ${showComments ? "active" : ""}`}
              onClick={() => setShowComments(!showComments)}
            >
              <i className="feather-message-circle"></i>
              <span>{Number(post.comment_count || 0)}</span>
            </button>

            <div className="share-action">
              <button
                type="button"
                className={`stat-btn ${shareOpen ? "active" : ""}`}
                onClick={() => setShareOpen(!shareOpen)}
              >
                <i className="feather-share-2"></i>
                <span>{shareCount}</span>
              </button>

              {shareOpen && (
                <div className="share-popup">
                  <div className="share-title-row">
                    <strong>Share post</strong>

                    <button
                      type="button"
                      className="share-close"
                      onClick={() => setShareOpen(false)}
                    >
                      ×
                    </button>
                  </div>

                  <div className="share-icons">
                    <button
                      type="button"
                      className="share-icon facebook"
                      onClick={() =>
                        openShare(
                          `https://www.facebook.com/sharer/sharer.php?u=${encodedShareUrl}`,
                          "facebook"
                        )
                      }
                    >
                      <FaIcon icon={FaFacebookF} />
                    </button>

                    <button
                      type="button"
                      className="share-icon twitter"
                      onClick={() =>
                        openShare(
                          `https://twitter.com/intent/tweet?url=${encodedShareUrl}&text=${encodedTitle}`,
                          "twitter"
                        )
                      }
                    >
                      <FaIcon icon={FaTwitter} />
                    </button>

                    <button
                      type="button"
                      className="share-icon whatsapp"
                      onClick={() =>
                        openShare(
                          `https://wa.me/?text=${encodeURIComponent(
                            `${pageTitle}\n${shareUrl}`
                          )}`,
                          "whatsapp"
                        )
                      }
                    >
                      <FaIcon icon={FaWhatsapp} />
                    </button>

                    <button
                      type="button"
                      className="share-icon linkedin"
                      onClick={() =>
                        openShare(
                          `https://www.linkedin.com/sharing/share-offsite/?url=${encodedShareUrl}`,
                          "linkedin"
                        )
                      }
                    >
                      <FaIcon icon={FaLinkedinIn} />
                    </button>

                    <button
                      type="button"
                      className="share-icon telegram"
                      onClick={() =>
                        openShare(
                          `https://t.me/share/url?url=${encodedShareUrl}&text=${encodedTitle}`,
                          "telegram"
                        )
                      }
                    >
                      <FaIcon icon={FaTelegramPlane} />
                    </button>
                  </div>

                  <div className="copy-box">
                    <span>{shareUrl}</span>

                    <button type="button" onClick={copyLink}>
                      {copied ? "Copied" : "Copy"}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {showComments && (
            <div className="comments-box">
              <div className="comments-title-row">
                <h4>Comments</h4>
                <span>{Number(post.comment_count || 0)}</span>
              </div>

              {post.comments && post.comments.length > 0 ? (
                post.comments.map((comment: any) => (
                  <div className="comment-item" key={comment.comment_id}>
                    <div className="comment-avatar">
                      {(comment.full_name || "G").charAt(0).toUpperCase()}
                    </div>

                    <div className="comment-content">
                      <div className="comment-top">
                        <strong>{comment.full_name || "Guest"}</strong>

                        {comment.created_at && (
                          <small>
                            {new Date(comment.created_at).toLocaleString(
                              "en-IN"
                            )}
                          </small>
                        )}
                      </div>

                      <p>{comment.comment_text}</p>
                    </div>
                  </div>
                ))
              ) : (
                <p className="no-comments">No comments yet</p>
              )}
            </div>
          )}
        </div>
      </div>

      <style>{`
        ${stateStyle}

        .news-details-page {
          min-height: 100vh;
          background:
            radial-gradient(circle at top left, rgba(59, 130, 246, 0.045), transparent 28%),
            #f4f7fb;
          padding: 20px 14px 26px;
          display: flex;
          justify-content: center;
          align-items: flex-start;
        }

        .news-details-card {
          width: min(100%, 720px);
          background: rgba(255, 255, 255, 0.98);
          border: 1px solid #e8edf5;
          border-radius: 17px;
          padding: 16px;
          box-shadow: 0 7px 22px rgba(15, 23, 42, 0.045);
        }

        .news-details-top {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 12px;
        }

        .news-back-btn {
          border: 1px solid #e2e8f0;
          background: #f8fafc;
          color: #475569;
          border-radius: 999px;
          padding: 7px 13px;
          font-size: 13px;
          font-weight: 600;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          gap: 5px;
          transition: all 0.2s ease;
        }

        .news-back-btn:hover {
          background: #eef4ff;
          border-color: #c7d2fe;
          color: #1d4ed8;
        }

        .news-details-header {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 10px 11px;
          border: 1px solid #eef2f7;
          border-radius: 13px;
          background: #fbfcfe;
          margin-bottom: 13px;
        }

        .news-details-avatar {
          width: 38px;
          height: 38px;
          border-radius: 50%;
          object-fit: cover;
          background: #e5e7eb;
          border: 1px solid #e2e8f0;
          flex-shrink: 0;
        }

        .news-source-info h4 {
          margin: 0;
          font-size: 14px;
          font-weight: 650;
          color: #111827;
        }

        .news-source-info p {
          margin: 2px 0 0;
          font-size: 12.5px;
          color: #64748b;
          font-weight: 500;
        }

        .news-details-title {
          font-size: clamp(21px, 2.4vw, 27px);
          line-height: 1.28;
          margin: 0 0 7px;
          color: #111827;
          font-weight: 630;
          letter-spacing: -0.015em;
        }

        .news-details-short {
          margin: 0 0 13px;
          color: #526174;
          font-size: 14.5px;
          font-weight: 500;
          line-height: 1.58;
        }

        .news-details-media,
        .news-details-youtube {
          margin-top: 11px;
        }

        .news-details-media img,
        .news-details-media video {
          width: 100%;
          border-radius: 13px;
          display: block;
          max-height: 470px;
          object-fit: cover;
          border: 1px solid #edf2f7;
          background: #f8fafc;
        }

        .news-details-youtube iframe {
          width: 100%;
          height: 340px;
          border: none;
          border-radius: 13px;
          background: #0f172a;
        }

        .news-details-content {
          margin-top: 14px;
          color: #334155;
          font-size: 14.5px;
          font-weight: 500;
          line-height: 1.68;
        }

        .news-details-content p {
          margin: 0 0 10px;
        }

        .news-details-content img {
          max-width: 100%;
          border-radius: 13px;
          margin: 11px 0;
          border: 1px solid #edf2f7;
        }

        .news-details-gallery {
          margin-top: 13px;
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(125px, 1fr));
          gap: 9px;
        }

        .news-details-gallery img {
          width: 100%;
          height: 112px;
          border-radius: 11px;
          object-fit: cover;
          border: 1px solid #edf2f7;
          background: #f8fafc;
        }

        .news-details-tags {
          display: flex;
          flex-wrap: wrap;
          gap: 7px;
          margin-top: 13px;
        }

        .news-details-tags span {
          border: 1px solid;
          border-radius: 999px;
          padding: 4px 9px;
          font-size: 12px;
          font-weight: 600;
          background: #f8fafc;
        }

        .news-donation-action {
          display: flex;
          justify-content: flex-end;
          margin-top: 14px;
        }

        .news-donate-btn {
          min-height: 42px;
          padding: 0 18px;
          border: none;
          border-radius: 10px;
          background: #2563eb;
          color: #ffffff;
          font-size: 14px;
          font-weight: 650;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 7px;
          box-shadow: 0 4px 12px rgba(37, 99, 235, 0.18);
          transition: all 0.2s ease;
        }

        .news-donate-btn:hover {
          background: #1d4ed8;
          transform: translateY(-1px);
        }

        .news-details-stats {
          display: flex;
          align-items: center;
          gap: 7px;
          border-top: 1px solid #edf2f7;
          margin-top: 14px;
          padding-top: 11px;
          position: relative;
        }

        .stat-item,
        .stat-btn {
          border: 0;
          background: transparent;
          display: inline-flex;
          align-items: center;
          gap: 6px;
          color: #64748b;
          font-size: 13px;
          font-weight: 600;
          cursor: pointer;
          padding: 6px 8px;
          border-radius: 999px;
          transition: all 0.2s ease;
        }

        .stat-btn:hover,
        .stat-btn.active {
          background: #f1f5f9;
          color: #1e293b;
        }

        .like-active {
          color: #e11d48;
        }

        .full-heart {
          color: #e11d48;
          font-size: 17px;
          line-height: 1;
        }

        .share-action {
          position: relative;
        }

        .share-popup {
          position: absolute;
          bottom: 42px;
          right: -18px;
          width: 300px;
          background: #fff;
          border-radius: 14px;
          padding: 13px;
          box-shadow: 0 16px 40px rgba(15, 23, 42, 0.13);
          border: 1px solid #e8edf5;
          z-index: 999;
        }

        .share-popup::after {
          content: "";
          position: absolute;
          bottom: -7px;
          right: 24px;
          width: 14px;
          height: 14px;
          background: #fff;
          transform: rotate(45deg);
          border-right: 1px solid #e8edf5;
          border-bottom: 1px solid #e8edf5;
        }

        .share-title-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 11px;
        }

        .share-title-row strong {
          font-size: 14px;
          font-weight: 650;
          color: #1f2937;
        }

        .share-close {
          width: 26px;
          height: 26px;
          border: 0;
          border-radius: 50%;
          background: #f1f5f9;
          color: #64748b;
          cursor: pointer;
          font-size: 19px;
          line-height: 1;
        }

        .share-icons {
          display: flex;
          align-items: center;
          gap: 9px;
          margin-bottom: 11px;
        }

        .share-icon {
          width: 38px;
          height: 38px;
          border-radius: 50%;
          border: none;
          color: #fff;
          display: grid;
          place-items: center;
          cursor: pointer;
          transition: transform 0.2s ease;
        }

        .share-icon:hover {
          transform: translateY(-2px);
        }

        .facebook {
          background: #1877f2;
        }

        .twitter {
          background: #1da1f2;
        }

        .whatsapp {
          background: #25d366;
        }

        .linkedin {
          background: #0077b5;
        }

        .telegram {
          background: #229ed9;
        }

        .copy-box {
          min-height: 40px;
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          border-radius: 10px;
          display: flex;
          align-items: center;
          overflow: hidden;
        }

        .copy-box span {
          flex: 1;
          padding: 0 10px;
          color: #64748b;
          font-size: 12.5px;
          font-weight: 500;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .copy-box button {
          align-self: stretch;
          border: none;
          border-left: 1px solid #e2e8f0;
          background: #fff;
          color: #2563eb;
          font-size: 12.5px;
          font-weight: 650;
          padding: 0 12px;
          cursor: pointer;
        }

        .comments-box {
          margin-top: 14px;
          padding-top: 12px;
          border-top: 1px solid #edf2f7;
        }

        .comments-title-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 10px;
        }

        .comments-title-row h4 {
          font-size: 14px;
          font-weight: 650;
          margin: 0;
          color: #1f2937;
        }

        .comments-title-row span {
          min-width: 24px;
          height: 24px;
          border-radius: 999px;
          background: #f1f5f9;
          color: #64748b;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-size: 12px;
          font-weight: 650;
          padding: 0 8px;
        }

        .comment-item {
          display: flex;
          align-items: flex-start;
          gap: 8px;
          margin-bottom: 8px;
        }

        .comment-avatar {
          width: 30px;
          height: 30px;
          border-radius: 50%;
          background: #eff6ff;
          color: #2563eb;
          display: grid;
          place-items: center;
          font-size: 12px;
          font-weight: 700;
          flex-shrink: 0;
          margin-top: 2px;
        }

        .comment-content {
          background: #f8fafc;
          border: 1px solid #edf2f7;
          border-radius: 12px;
          padding: 8px 10px;
          flex: 1;
          min-width: 0;
        }

        .comment-top {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 10px;
          margin-bottom: 3px;
        }

        .comment-content strong {
          display: block;
          font-size: 13px;
          color: #111827;
          font-weight: 650;
          line-height: 1.2;
        }

        .comment-content p {
          margin: 0;
          font-size: 13.5px;
          color: #475569;
          font-weight: 500;
          line-height: 1.4;
        }

        .comment-content small {
          color: #7c8da5;
          font-size: 11.5px;
          font-weight: 500;
          white-space: nowrap;
          flex-shrink: 0;
        }

        .no-comments {
          font-size: 13px;
          color: #94a3b8;
          font-weight: 500;
          margin: 0;
        }

        @media (min-width: 1200px) {
          .news-details-page {
            padding-top: 22px;
            padding-left: 14px;
            padding-right: 14px;
          }

          .news-details-card {
            width: 720px;
          }
        }

        @media (max-width: 768px) {
          .news-details-page {
            padding: 10px 9px 22px;
          }

          .news-details-card {
            width: 100%;
            border-radius: 15px;
            padding: 13px;
          }

          .news-details-header {
            padding: 9px 10px;
            border-radius: 12px;
          }

          .news-details-title {
            font-size: 21px;
          }

          .news-details-short,
          .news-details-content {
            font-size: 14px;
          }

          .news-details-youtube iframe {
            height: 230px;
          }

          .news-details-media img,
          .news-details-media video {
            max-height: 420px;
          }

          .news-donation-action {
          display: flex;
          justify-content: flex-end;
          margin-top: 14px;
        }

        .news-donate-btn {
          min-height: 42px;
          padding: 0 18px;
          border: none;
          border-radius: 10px;
          background: #2563eb;
          color: #ffffff;
          font-size: 14px;
          font-weight: 650;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 7px;
          box-shadow: 0 4px 12px rgba(37, 99, 235, 0.18);
          transition: all 0.2s ease;
        }

        .news-donate-btn:hover {
          background: #1d4ed8;
          transform: translateY(-1px);
        }

        .news-details-stats {
            gap: 4px;
            overflow-x: auto;
            scrollbar-width: none;
          }

          .news-details-stats::-webkit-scrollbar {
            display: none;
          }

          .stat-item,
          .stat-btn {
            padding: 7px 8px;
            white-space: nowrap;
          }

          .news-donation-action {
            justify-content: stretch;
          }

          .news-donate-btn {
            width: 100%;
          }

          .share-popup {
            position: fixed;
            left: 12px;
            right: 12px;
            bottom: 20px;
            width: auto;
            border-radius: 18px;
          }

          .share-popup::after {
            display: none;
          }

          .share-icons {
            justify-content: space-between;
          }

          .share-icon {
            width: 40px;
            height: 40px;
          }

          .comment-top {
            display: block;
          }

          .comment-content small {
            display: block;
            margin-top: 3px;
            white-space: normal;
          }
        }
      `}</style>
    </>
  );
};

const stateStyle = `
  .news-state-page {
    min-height: 100vh;
    background: #f4f7fb;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 24px;
  }

  .news-state-card {
    width: 100%;
    max-width: 350px;
    min-height: 230px;
    background: #fff;
    border: 1px solid #e8edf5;
    border-radius: 18px;
    box-shadow: 0 10px 28px rgba(15, 23, 42, 0.055);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 10px;
    text-align: center;
    padding: 24px;
  }

  .news-loader {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    border: 4px solid #dbeafe;
    border-top-color: #2563eb;
    animation: newsSpin 0.8s linear infinite;
    margin-bottom: 8px;
  }

  @keyframes newsSpin {
    to {
      transform: rotate(360deg);
    }
  }

  .news-empty-icon {
    width: 50px;
    height: 50px;
    border-radius: 50%;
    background: #fee2e2;
    color: #dc2626;
    display: grid;
    place-items: center;
    font-size: 25px;
    font-weight: 750;
    margin-bottom: 6px;
  }

  .news-state-card h3 {
    margin: 0;
    font-size: 18px;
    color: #1f2937;
    font-weight: 650;
  }

  .news-state-card p {
    margin: 0;
    font-size: 14px;
    line-height: 1.55;
    color: #64748b;
    font-weight: 500;
  }

  .news-state-btn {
    margin-top: 13px;
    height: 39px;
    padding: 0 19px;
    border: none;
    border-radius: 999px;
    background: #2563eb;
    color: #fff;
    font-size: 13px;
    font-weight: 650;
    cursor: pointer;
  }
`;

export default NewsDetails;